import styled from '@emotion/styled';
import { addDays } from 'date-fns';
import _ from 'lodash';
import { Info, Paperclip, UserPlus } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { msg, ph } from '@lingui/core/macro';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import applicationsResource from 'modules/application-link/domain/resources/applications.resource';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { championshipSettingsSelector } from 'modules/championship/store/championship/selectors';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { mapPlayerToAnnounced } from 'modules/teams/domain/data-mappers/announced-teams.mapper';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { PlayerApplication } from 'modules/teams/domain/interfaces/player-application';
import { toastApplicationSaveError } from 'modules/teams/ui/utils';
import {
  getChampionshipTeamsV2,
  updateChampionshipPlayerApplicationDetails,
  upsertChampionshipApplicationUsers,
} from 'modules/teams/store/championship-teams/actions';
import {
  announcedChampionshipTeamsDataSelector,
  announcedChampionshipTeamsIsLoadingSelector,
} from 'modules/teams/store/championship-teams/selectors';
import { useGetTeamByIdQuery } from 'modules/teams/store/teams-api';
import { trackEvent } from 'shared/analytics';
import { deriveAsyncState } from 'shared/utils/async-state';
import { useAppDispatch } from 'store/hooks';
import { theme } from 'styles/theme/theme';
import { UDEntityList, UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import TeamLogo from 'ud-ui/components/team-logo';
import UDTippy from 'ud-ui/components/tippy';
import ApplicationStatusChips, { ApplicationStatusChipOption } from './application-status-chips';
import CreatePlayerModal from './create-player-modal';
import PlayerApplicationRow, { PlayerApplicationDetails } from './player-application-row';
import useDragSelection from './use-drag-selection';

/** Фильтр статуса. «Все» по умолчанию: заявленный игрок не должен исчезать с экрана. */
type PlayersFilter = 'all' | ApplicationStatus;

/** Тот же дефолт лимита, что и в v1: настройка турнира может быть не заполнена. */
const DEFAULT_MAX_PLAYERS = 28;

interface ApplicationsTeamScreenProps {
  championshipId: number;
}

/**
 * Название команды в шапке — ссылка на её карточку.
 *
 * Подчёркивание задаётся Emotion, а не tailwind-классом `hover:underline`: двоеточие в имени
 * класса ломает разбор селектора `:has(a:hover, …)` из `UDEntityRow` в jsdom, и тесты падают
 * с `SyntaxError: not a valid selector` при поиске по роли.
 */
const TeamNameLink = styled(Link)`
  &:hover {
    text-decoration: underline;
  }
`;

const matchesFilter = (application: PlayerApplication, filter: PlayersFilter) => filter === 'all' || application.status === filter;

/**
 * Экран состава одной команды в заявках, второй флоу (v2).
 *
 * Живёт по тому же адресу, что и `championship-team-players` (`…/applications/:teamId`);
 * какой из двух рисуется, решает фиче-флаг `applicationsFlowV2` в
 * `championship-applications-page.tsx`.
 *
 * Отличий от v1 четыре, и все — про «что делать дальше»:
 *
 * - массовые кнопки «Заявить» и «Отклонить» видны всегда и просто отключены без выбора
 *   (в v1 их контейнер схлопнут в нулевую ширину, пока никто не выбран);
 * - фильтр по умолчанию «Все», а не «Незаявленные» — принятый игрок остаётся на экране;
 * - в шапке видно, сколько игроков заявлено и каков лимит турнира;
 * - пустой состав зовёт добавить игрока или отправить заявочную ссылку, а не пишет «Список пуст».
 *
 * Заявки соревнования экран догружает сам: на этот адрес можно попасть по прямой ссылке,
 * минуя список команд. Ошибку загрузки слайс не хранит, поэтому её ветку экран держит у себя —
 * тот же приём, что в `applications-teams-screen.tsx`.
 */
const ApplicationsTeamScreen = ({ championshipId }: ApplicationsTeamScreenProps) => {
  const { t } = useLingui();
  const dispatch = useAppDispatch();
  const { teamId: teamIdParam } = useParams();
  const teamId = Number(teamIdParam);

  const [filter, setFilter] = useState<PlayersFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isError, setIsError] = useState(false);

  const isTeamsLoading = useSelector(announcedChampionshipTeamsIsLoadingSelector);
  // Селектор — фабрика, и звать её надо **с** `championshipId`: без аргумента
  // (`team-players-page.tsx:22`) в компонент приезжает сама функция, а не данные.
  const announcedTeams = useSelector(announcedChampionshipTeamsDataSelector(championshipId));
  const settings = useSelector(championshipSettingsSelector(championshipId));
  const tournamentId = useSelector(authStoreUserTournamentId);

  const announcedTeam = announcedTeams ? announcedTeams[teamId] : null;
  const maxPlayers = settings?.applicationMaxPlayers ?? DEFAULT_MAX_PLAYERS;

  useEffect(() => {
    if (announcedTeams) {
      return;
    }

    setIsError(false);
    dispatch(getChampionshipTeamsV2({ championshipId }))
      .unwrap()
      .catch(() => setIsError(true));
  }, [announcedTeams, championshipId, dispatch]);

  const { isLoading: isTeamLoading, data: detailTeam } = useGetTeamByIdQuery(teamId, { skip: !teamId });

  // Состав команды и заявки — два разных источника: игрок, у которого заявки ещё нет,
  // приходит только из `getTeamById`. Заявка перекрывает запись состава, поэтому она
  // первым аргументом `uniqBy` (перенесено из v1 без изменений).
  const mergedPlayers = useMemo(() => {
    if (isTeamLoading || !announcedTeam || !detailTeam?.players) {
      return [];
    }

    const notRegisteredApplications = mapPlayerToAnnounced(detailTeam.players, {
      teamId,
      championshipId,
      teamCreatedAt: detailTeam.createdAt,
    });

    return _.uniqBy([...Object.values(announcedTeam.applications || []), ...notRegisteredApplications], 'player.id').filter(
      (application) => !application.player.isReal,
    );
  }, [isTeamLoading, announcedTeam, detailTeam?.players, detailTeam?.createdAt, teamId, championshipId]);

  const acceptedCount = useMemo(
    () => mergedPlayers.filter((application) => application.status === ApplicationStatus.ACCEPTED).length,
    [mergedPlayers],
  );

  const countByFilter = useMemo(
    () =>
      mergedPlayers.reduce<Record<PlayersFilter, number>>(
        (result, application) => {
          result.all += 1;
          result[application.status] += 1;
          return result;
        },
        {
          all: 0,
          [ApplicationStatus.PENDING]: 0,
          [ApplicationStatus.ACCEPTED]: 0,
          [ApplicationStatus.DECLINED]: 0,
        },
      ),
    [mergedPlayers],
  );

  const visiblePlayers = useMemo(() => {
    const query = search.trim().toLowerCase();

    return mergedPlayers.filter((application) => {
      if (!matchesFilter(application, filter)) {
        return false;
      }

      const firstName = application.player.firstName.toLowerCase();
      const lastName = application.player.lastName.toLowerCase();

      return firstName.includes(query) || lastName.includes(query);
    });
  }, [mergedPlayers, filter, search]);

  // Ветку рендера выбираем по **отфильтрованному** списку, иначе «ничего не найдено»
  // не покажется никогда (`.claude/rules/conventions.md`).
  const state = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: visiblePlayers.length,
        hasCurrentData: Boolean(announcedTeam) && !isTeamLoading,
        isFetching: isTeamsLoading || isTeamLoading,
        isError,
      }),
    [visiblePlayers.length, announcedTeam, isTeamLoading, isTeamsLoading, isError],
  );

  const togglePlayer = useCallback((playerId: number) => {
    setSelectedIds((current) => (current.includes(playerId) ? current.filter((id) => id !== playerId) : [...current, playerId]));
  }, []);
  // Выбор читается через ref: жест спрашивает состояние строки в момент нажатия, а сами
  // обработчики от этого меняться не должны — иначе React.memo строк пересоздаёт их на
  // каждом изменении выбора.
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const isPlayerSelected = useCallback((playerId: number) => selectedIdsRef.current.includes(playerId), []);

  const setPlayerSelected = useCallback((playerId: number, selected: boolean) => {
    setSelectedIds((current) => {
      if (current.includes(playerId) === selected) {
        return current;
      }

      return selected ? [...current, playerId] : current.filter((id) => id !== playerId);
    });
  }, []);

  const { isDragging, handleRowMouseDown, handleRowMouseEnter, handleRowClick } = useDragSelection({
    isSelected: isPlayerSelected,
    setSelected: setPlayerSelected,
  });

  const changeStatus = useCallback(
    async (status: ApplicationStatus.ACCEPTED | ApplicationStatus.DECLINED, playerIds: number[]) => {
      if (!announcedTeam || playerIds.length === 0) {
        return;
      }

      // Нечего менять — молча не уходим на бэкенд, но и не молчим: кнопка активна, клик был.
      // Проверка идёт выше проверки лимита: «все и так заявлены» лимит превысить не может,
      // а обратный порядок дал бы ошибку про лимит на действии, которое ничего не меняет.
      // Условие — именно «ни у кого не отличается»: частичный выбор уходит как раньше,
      // потому что мутация принимает полный состав, а не дельту.
      const hasSomethingToChange = mergedPlayers.some(
        (application) => playerIds.includes(application.player.id) && application.status !== status,
      );

      if (!hasSomethingToChange) {
        // Выбор не сбрасываем: пользователь, скорее всего, ошибся кнопкой и сейчас нажмёт соседнюю.
        toast.info(
          status === ApplicationStatus.ACCEPTED ? t`Все выбранные игроки уже заявлены` : t`Все выбранные игроки уже отклонены`,
        );
        return;
      }

      const applications = mergedPlayers.map((application) => ({
        userId: application.player.id,
        name: application.player.firstName,
        surname: application.player.lastName,
        middleName: application.player.middleName,
        birthDate: application.player.birthDate,
        position: application.position,
        number: application.number,
        state: playerIds.includes(application.player.id) ? status : application.status,
      }));

      // Лимит проверяем до отправки: бэкенд вернёт ту же ошибку, но текстом, который
      // ещё надо разбирать регуляркой (`team-player-card.tsx:178`).
      if (status === ApplicationStatus.ACCEPTED) {
        const nextAccepted = applications.filter((application) => application.state === ApplicationStatus.ACCEPTED).length;

        if (nextAccepted > maxPlayers) {
          toast.error(t`Заявить можно не больше ${ph({ limit: maxPlayers })} игроков. Снимите лишних или измените условия турнира.`);
          return;
        }
      }

      // Тот же признак, что и у `hasSomethingToChange` выше, только числом: считаем игроков,
      // у которых статус реально сменится, а не всех выбранных.
      const newlyAccepted =
        status === ApplicationStatus.ACCEPTED
          ? mergedPlayers.filter(
              (application) => playerIds.includes(application.player.id) && application.status !== ApplicationStatus.ACCEPTED,
            ).length
          : 0;

      setIsSubmitting(true);
      try {
        await dispatch(upsertChampionshipApplicationUsers({ teamId, championshipId, applications })).unwrap();

        // Экшен общий с v1 и в конце сам зовёт getChampionshipAnnouncedTeams (actions.ts:216),
        // чей fulfilled заменяет ветку соревнования картой «только команды с заявками» — команды без
        // заявок из стора исчезают. Перечитываем источником v2 после него, а не вместо.
        await dispatch(getChampionshipTeamsV2({ championshipId }));

        StepperMainTool.setByPath('stepper:main:applicationTeam', true);
        trackEvent('Team Application Submitted', { championship_id: championshipId, team_id: teamId, flow: 'v2', source: 'bulk' });
        if (newlyAccepted > 0) {
          trackEvent('Player Application Accepted', {
            championship_id: championshipId,
            team_id: teamId,
            flow: 'v2',
            source: 'bulk',
            players_count: newlyAccepted,
          });
        }
        toast.success(t`Игроки обновлены`);
        setSelectedIds([]);
      } catch (error) {
        // Отказ 7016 — игрок уже заявлен в другой команде: бэкенд называет и игрока, и команду.
        toastApplicationSaveError(error, msg`Не удалось обновить заявки игроков`);
      } finally {
        setIsSubmitting(false);
      }
    },
    [announcedTeam, mergedPlayers, maxPlayers, dispatch, teamId, championshipId, t],
  );

  /**
   * Номер и амплуа из строки состава.
   *
   * Мутация построчная (`upsertChampionshipApplicationForUser`): батчевая
   * `upsertChampionshipApplication` умеет только статус — `number`/`position` в ней
   * закомментированы (`teams.repository.ts:113-125`).
   *
   * Игроку, у которого заявки ещё нет (пришёл из состава команды), бэкенд заведёт её
   * в статусе `PENDING`: хранить номер иначе негде. Это осознанный побочный эффект
   * явного действия пользователя — при загрузке экрана заявки по-прежнему не создаются.
   */
  const changeDetails = useCallback(
    async (application: PlayerApplication, details: PlayerApplicationDetails) => {
      try {
        await dispatch(
          updateChampionshipPlayerApplicationDetails({
            teamId,
            championshipId,
            createUserOrUpdateApplication: {
              userId: application.player.id,
              name: application.player.firstName,
              surname: application.player.lastName,
              middleName: application.player.middleName,
              birthDate: application.player.birthDate,
              position: details.position?.toUpperCase(),
              number: details.number,
              state: application.status,
            },
          }),
        ).unwrap();

        toast.success(t`Данные игрока сохранены`);
        return true;
      } catch (error) {
        toastApplicationSaveError(error, msg`Не удалось сохранить данные игрока`);
        return false;
      }
    },
    [dispatch, teamId, championshipId, t],
  );

  const acceptSelected = useCallback(() => changeStatus(ApplicationStatus.ACCEPTED, selectedIds), [changeStatus, selectedIds]);
  const declineSelected = useCallback(() => changeStatus(ApplicationStatus.DECLINED, selectedIds), [changeStatus, selectedIds]);
  const acceptAll = useCallback(
    () =>
      changeStatus(
        ApplicationStatus.ACCEPTED,
        mergedPlayers.map((application) => application.player.id),
      ),
    [changeStatus, mergedPlayers],
  );

  // Даты — через `useMemo`: без него `new Date(...)` даёт новый объект каждый рендер,
  // и зависимости `handleCopyLink` меняются на каждом.
  const startDate = useMemo(
    () => (settings?.applicationStartDate ? new Date(settings.applicationStartDate) : undefined),
    [settings?.applicationStartDate],
  );
  const endDate = useMemo(
    () => (settings?.applicationEndDate ? new Date(settings.applicationEndDate) : undefined),
    [settings?.applicationEndDate],
  );
  const now = new Date();
  const isAfterStartDate = startDate ? startDate < now : true;
  const isBeforeEndDate = endDate ? endDate > now : true;
  const isRangeValid = isAfterStartDate && isBeforeEndDate;

  const handleCopyLink = useCallback(async () => {
    if (endDate && endDate < new Date()) {
      toast.warning(t`Заявочная кампания закончилась. Чтобы получить ссылку, продлите дату окончания приёма заявок.`);
      return;
    }

    const token = await applicationsResource.getToken(championshipId, teamId);
    const params = new URLSearchParams();
    params.append('token', token.token);
    navigator.clipboard.writeText(`${window.location.origin}${Pages.APPLICATION_LINK.INDEX}?${params.toString()}`);

    const expiresAt = endDate ?? addDays(new Date(), 1);
    toast.success(
      t`Ссылка скопирована и будет активна до ${ph({ date: expiresAt.toLocaleDateString() })} ${ph({ time: expiresAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) })}`,
    );
  }, [championshipId, teamId, endDate, t]);

  const chipOptions = useMemo<ApplicationStatusChipOption<PlayersFilter>[]>(
    () => [
      { value: 'all', label: t`Все`, count: countByFilter.all },
      {
        value: ApplicationStatus.PENDING,
        label: t`Ожидают решения`,
        count: countByFilter[ApplicationStatus.PENDING],
        tone: 'pending',
      },
      {
        value: ApplicationStatus.ACCEPTED,
        label: t`Заявлены`,
        count: countByFilter[ApplicationStatus.ACCEPTED],
        tone: 'accepted',
      },
      {
        value: ApplicationStatus.DECLINED,
        label: t`Отклонены`,
        count: countByFilter[ApplicationStatus.DECLINED],
        tone: 'declined',
      },
    ],
    [t, countByFilter],
  );

  const openAddPlayer = useCallback(() => setIsAddingPlayer(true), []);
  const closeAddPlayer = useCallback(() => setIsAddingPlayer(false), []);

  // Игрок заведён — перечитываем заявки соревнования: в сторе он уже дорисован
  // редьюсером `addPlayerApplication`, но состав команды и статусы приезжают с сервера.
  const handlePlayerCreated = useCallback(() => {
    dispatch(getChampionshipTeamsV2({ championshipId }));
  }, [dispatch, championshipId]);

  const addPlayerButton = useMemo(
    () => (
      <UDButton className="flex items-center" variant="dark" onClick={openAddPlayer}>
        <UserPlus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
        <span>{t`Добавить игрока`}</span>
      </UDButton>
    ),
    [t, openAddPlayer],
  );

  // Массовые кнопки видны всегда: это главное действие экрана, и прятать его до выбора
  // означает, что пользователь о нём не узнает. Без выбора они просто отключены.
  const bulkActions = useMemo(
    () => (
      <>
        <UDButton variant="primary" disabled={selectedIds.length === 0 || isSubmitting} onClick={acceptSelected}>
          {t`Заявить`}
        </UDButton>
        <UDButton variant="secondary" disabled={selectedIds.length === 0 || isSubmitting} onClick={declineSelected}>
          {t`Отклонить`}
        </UDButton>
        {addPlayerButton}
      </>
    ),
    [t, selectedIds.length, isSubmitting, acceptSelected, declineSelected, addPlayerButton],
  );

  const renderPlayer = useCallback(
    (application: PlayerApplication, index: number) => (
      <PlayerApplicationRow
        application={application}
        index={index + 1}
        isSelected={selectedIds.includes(application.player.id)}
        onToggle={togglePlayer}
        onRowMouseDown={handleRowMouseDown}
        onRowMouseEnter={handleRowMouseEnter}
        onRowClick={handleRowClick}
        onChangeDetails={changeDetails}
      />
    ),
    [selectedIds, togglePlayer, handleRowMouseDown, handleRowMouseEnter, handleRowClick, changeDetails],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col px-6 py-4" data-testid="applications-team-screen-v2">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <TeamLogo size={60} url={announcedTeam?.logo?.url} />
          <div>
            {/*
             * Название команды — ссылка на её карточку: из заявок надо уметь дойти до состава
             * и логотипа команды, не уходя в раздел «Команды» искать её там. В строке списка
             * заявок название ссылкой не делаем — там клик по строке уже открывает заявку.
             */}
            <TeamNameLink to={Pages.TEAMS.builders.edit(teamId)}>
              <UDText type="subhead">{announcedTeam?.name ?? ''}</UDText>
            </TeamNameLink>
            <UDText type="footnote" className="text-surfaceRomanSilver30">
              {t`Заявлено ${ph({ accepted: acceptedCount })} из ${ph({ limit: maxPlayers })} (лимит турнира)`}
            </UDText>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <UDButton variant="primary" disabled={mergedPlayers.length === 0 || isSubmitting} onClick={acceptAll}>
            {t`Заявить всех`}
          </UDButton>

          <UDButton className="flex items-center" variant="secondary" disabled={!isRangeValid} onClick={handleCopyLink}>
            <Paperclip size={16} className="mr-2" />
            <span>{t`Скопировать ссылку для заявки`}</span>
          </UDButton>

          <UDTippy
            interactive
            content={
              <UDText type="subhead">
                {isRangeValid &&
                  t`Ссылка для заявки будет скопирована в буфер обмена. Отправьте её в чат, чтобы игроки заполнили данные сами.`}
                {!isAfterStartDate && (
                  <div>
                    <Link
                      className="text-LightBlue"
                      to={Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules')}
                      state={{ highlight: { applicationStartDate: true } }}
                    >
                      {t`Заявочная кампания`}
                    </Link>{' '}
                    {t`ещё не началась. Дата начала ${ph({ date: startDate?.toLocaleDateString() ?? '' })}. Для копирования ссылки измените дату начала приёма заявок.`}
                  </div>
                )}
                {!isBeforeEndDate && (
                  <div>
                    <Link
                      className="text-LightBlue"
                      to={Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules')}
                      state={{ highlight: { applicationEndDate: true } }}
                    >
                      {t`Заявочная кампания`}
                    </Link>{' '}
                    {t`закончилась. Дата окончания ${ph({ date: endDate?.toLocaleDateString() ?? '' })}. Для копирования ссылки продлите дату окончания приёма заявок.`}
                  </div>
                )}
              </UDText>
            }
          >
            <Info size={16} />
          </UDTippy>
        </div>
      </div>

      <UDEntityList<PlayerApplication>
        items={visiblePlayers}
        renderItem={renderPlayer}
        getItemKey={(application) => application.player.id}
        state={state}
        // На время протягивания браузер иначе выделяет текст строк.
        listClassName={isDragging ? 'select-none' : undefined}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t`Поиск по игроку`}
        extraFilters={
          mergedPlayers.length > 0 ? <ApplicationStatusChips options={chipOptions} value={filter} onChange={setFilter} /> : undefined
        }
        // Чип сужает выдачу, только пока он нарисован: без игроков список пуст сам по себе.
        hasActiveFilters={mergedPlayers.length > 0 && filter !== 'all'}
        // Кнопки либо в тулбаре, либо в пустом состоянии, но не в обоих сразу.
        actions={mergedPlayers.length > 0 ? bulkActions : undefined}
        emptyText={t`В команде нет игроков`}
        emptySubtitle={t`Добавьте игрока вручную или отправьте заявочную ссылку — игроки заполнят данные сами.`}
        emptyAction={addPlayerButton}
        notFoundText={t`Игроки не найдены`}
        errorText={t`Не удалось загрузить состав команды`}
      />

      {isAddingPlayer && tournamentId && (
        <CreatePlayerModal
          tournamentId={tournamentId}
          teamId={teamId}
          players={detailTeam?.players ?? []}
          onCreated={handlePlayerCreated}
          onClose={closeAddPlayer}
        />
      )}
    </div>
  );
};

export default ApplicationsTeamScreen;
